import { useRef, useCallback, useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { t, type TranslationKey } from "@/lib/i18n";
import { useLocale } from "@/hooks/use-locale";
import { sectorPath, labelArcPath, polar } from "@/lib/utils/donut";

const BRAND = "hsl(var(--brand))";
const SIZE = 860;
const OUTER_R = SIZE / 2;                          // 430 - bigger ring reaches into the gap
const INNER_R = Math.round(OUTER_R * 0.40);        // ~172 - small centre hole, wide band
const LABEL_R = Math.round((OUTER_R + INNER_R) / 2); // ~301 - mid-band radius for labels
const GAP_DEG = 2.6;                               // angular gap between slices (pizza look)

interface Tool {
  id: string;
  icon: LucideIcon;
  label: string;
  /** Small corner badge text, e.g. "NEW". */
  badge?: string;
  /** If set, the entry navigates to this route instead of opening a tool. */
  route?: string;
}

interface DonutWheelProps {
  tools: Tool[];
  activeTool: string | null;
  onToolSelect: (id: string) => void;
  onDeselect: () => void;
}

const DonutWheel = ({ tools, activeTool, onToolSelect, onDeselect }: DonutWheelProps) => {
  useLocale(); // re-render labels on language change

  const ringRef      = useRef<SVGGElement>(null);
  const labelRefs    = useRef<(SVGGElement | null)[]>([]);
  const current      = useRef(0);
  const target       = useRef(0);
  const raf          = useRef<number | null>(null);
  const touchY       = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  const applyAngle = useCallback((angle: number) => {
    if (ringRef.current) {
      ringRef.current.setAttribute("transform", `rotate(${angle} ${OUTER_R} ${OUTER_R})`);
    }
    // Labels live OUTSIDE the rotating ring: reposition them to their slice's
    // live screen angle but keep them perfectly horizontal - readable at any spin.
    const step = 360 / tools.length;
    labelRefs.current.forEach((el, i) => {
      if (!el) return;
      const mid = (i + 0.5) * step - 90;            // slice centroid (rest)
      const [x, y] = polar(OUTER_R, OUTER_R, LABEL_R, mid + angle);
      el.setAttribute("transform", `translate(${x} ${y})`);
    });
  }, [tools.length]);

  const animate = useCallback(() => {
    if (raf.current) cancelAnimationFrame(raf.current);
    const tick = () => {
      const diff = target.current - current.current;
      if (Math.abs(diff) < 0.02) {
        current.current = target.current;
        applyAngle(current.current);
        raf.current = null;
        return;
      }
      current.current += diff * 0.1;
      applyAngle(current.current);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  }, [applyAngle]);

  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    target.current += e.deltaY * 0.14;
    animate();
  }, [animate]);

  const onTouchStart = useCallback((e: TouchEvent) => {
    touchY.current = e.touches[0].clientY;
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (touchY.current === null) return;
    const newY = e.touches[0].clientY;
    const dy = newY - touchY.current;
    touchY.current = newY;
    // Negative so the visible (left) arc follows the finger: drag down -> the
    // slices under your finger move down. The mouse wheel (onWheel) keeps its
    // own scroll convention. Fixes the reversed swipe on touch tablets.
    target.current -= dy * 0.55;
    animate();
  }, [animate]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [onWheel, onTouchStart, onTouchMove]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      target.current += 360 / tools.length;
      animate();
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      target.current -= 360 / tools.length;
      animate();
    } else if (e.key === "Escape" && activeTool) {
      e.preventDefault();
      onDeselect();
    }
  }, [tools.length, animate, activeTool, onDeselect]);

  useEffect(() => { applyAngle(0); }, [applyAngle]);

  // Auto-center: when a tool becomes active, rotate the wheel so its slice sits
  // at the left-middle of the clipped window (screen angle 180deg). Picks the
  // equivalent angle nearest the current one so it spins the short way.
  useEffect(() => {
    if (!activeTool) return;
    const i = tools.findIndex(tl => tl.id === activeTool);
    if (i < 0) return;
    const step = 360 / tools.length;
    let next = 270 - (i + 0.5) * step;            // brings slice i's mid to 180deg
    while (next - current.current > 180)  next -= 360;
    while (next - current.current < -180) next += 360;
    target.current = next;
    animate();
  }, [activeTool, tools, animate]);

  const step = 360 / tools.length;

  // Render order: hovered slice last so it paints above its neighbours.
  const ordered = tools
    .map((tool, i) => ({ tool, i }))
    .sort((a, b) => (a.tool.id === hovered ? 1 : 0) - (b.tool.id === hovered ? 1 : 0));

  return (
    <div
      ref={containerRef}
      className="relative flex-1 h-full flex items-center justify-end focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand))] focus-visible:ring-offset-2"
      style={{ cursor: "ns-resize" }}
      role="listbox"
      aria-label="Tool selector - scroll, swipe or use arrow keys to rotate"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      {/* Scroll hint - subtle vertical arrows on the visible edge */}
      <div
        className="absolute flex flex-col items-center gap-1.5 z-20 pointer-events-none select-none"
        style={{ right: 20, top: "50%", transform: "translateY(-50%)", opacity: 0.25 }}
        aria-hidden="true"
      >
        <svg width="8" height="6" viewBox="0 0 8 6"><path d="M4 0L8 6H0z" fill="currentColor" /></svg>
        <div style={{ width: 1, height: 32, background: "currentColor" }} />
        <svg width="8" height="6" viewBox="0 0 8 6"><path d="M4 6L0 0H8z" fill="currentColor" /></svg>
      </div>

      {/* Donut - ~40% visible (60% clipped off-screen right) */}
      <div
        className="relative flex-shrink-0"
        style={{ width: SIZE, height: SIZE, marginRight: -Math.round(SIZE * 0.60) }}
      >
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{ overflow: "visible", display: "block" }}
        >
          <g ref={ringRef}>
            {ordered.map(({ tool, i }) => {
              const a0 = i * step - 90 + GAP_DEG / 2;
              const a1 = (i + 1) * step - 90 - GAP_DEG / 2;
              const isActive = tool.id === activeTool;
              const label = t(`tool.${tool.id}` as TranslationKey);
              const d = sectorPath(OUTER_R, OUTER_R, INNER_R, OUTER_R, a0, a1);

              return (
                <g
                  key={tool.id}
                  className="donut-slice"
                  role="option"
                  aria-selected={isActive}
                  aria-label={label}
                  onMouseEnter={() => setHovered(tool.id)}
                  onMouseLeave={() => setHovered(prev => (prev === tool.id ? null : prev))}
                  onClick={() => onToolSelect(tool.id)}
                >
                  {/* Visual layer - scales on hover, never hit-tested (no pointer events) */}
                  <g className="donut-slice-visual">
                    <path
                      d={d}
                      fill={BRAND}
                      fillOpacity={isActive ? 1 : 0.92}
                      stroke="hsl(var(--background))"
                      strokeWidth={2}
                      style={isActive ? { filter: "brightness(1.12)" } : undefined}
                    />
                    {/* Active ring accent on the outer edge */}
                    {isActive && (
                      <path
                        d={labelArcPath(OUTER_R, OUTER_R, OUTER_R - 3, a0, a1)}
                        fill="none"
                        stroke="rgba(255,255,255,0.85)"
                        strokeWidth={2}
                      />
                    )}
                  </g>
                  {/* Stable hit target at REST geometry - keeps :hover steady even
                      while the visual scales, so the edge no longer flicker-loops. */}
                  <path className="donut-slice-hit" d={d} />
                </g>
              );
            })}
          </g>

          {/* Label layer - OUTSIDE the rotating ring. Each label travels with its
              slice (position set in applyAngle) but stays horizontal & upright,
              so it reads the same whether the wheel is scrolled forward or back.
              Each label = tool icon stacked above its name, centred on the mid-band. */}
          <g style={{ pointerEvents: "none" }}>
            {tools.map((tool, i) => {
              const isActive = tool.id === activeTool;
              const Icon = tool.icon;
              const mid = (i + 0.5) * step - 90;
              const [lx, ly] = polar(OUTER_R, OUTER_R, LABEL_R, mid + current.current);
              return (
                <g
                  key={tool.id}
                  ref={el => { labelRefs.current[i] = el; }}
                  transform={`translate(${lx} ${ly})`}
                  style={{ color: isActive ? "#fff" : "hsl(var(--brand-fg))" }}
                >
                  <Icon x={-11} y={-26} width={22} height={22} strokeWidth={2.1} />
                  <text
                    className="donut-slice-label"
                    x={0}
                    y={11}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="currentColor"
                    fontSize={15}
                  >
                    {t(`tool.${tool.id}` as TranslationKey)}
                  </text>
                  {tool.badge && (
                    <g transform="translate(14 -30)">
                      <rect x={-15} y={-8} width={30} height={15} rx={7.5} fill="#fff" stroke="hsl(var(--brand))" strokeWidth={1} />
                      <text x={0} y={0} textAnchor="middle" dominantBaseline="central" fontSize={8} fontWeight={900} fill="hsl(var(--brand))" style={{ letterSpacing: "0.08em" }}>
                        {tool.badge}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* Inner hole - small centre circle; reset hint when a tool is active */}
        <div
          className="absolute rounded-full bg-background flex items-center justify-center"
          style={{
            width: INNER_R * 2,
            height: INNER_R * 2,
            top:  OUTER_R - INNER_R,
            left: OUTER_R - INNER_R,
            zIndex: 20,
            cursor: activeTool ? "pointer" : "default",
          }}
          onClick={activeTool ? onDeselect : undefined}
          title={activeTool ? "Click to deselect" : undefined}
        >
          {activeTool && (
            <span
              className="pointer-events-none select-none text-center"
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "hsl(var(--muted-foreground))",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                padding: "0 10px",
              }}
            >
              {t("donut.reset")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonutWheel;
